"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import {
  MessageCircle,
  Briefcase,
  Mic,
  Users,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";

interface QuickAction {
  label: string;
  description: string;
  href: string;
  icon: LucideIcon;
}

interface Props {
  open: boolean;
  onClose: () => void;
  profileEditHref: string;
}

export default function MobileQuickActionSheet({
  open,
  onClose,
  profileEditHref,
}: Props) {
  const router = useRouter();
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const firstActionRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    firstActionRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const actions: QuickAction[] = [
    {
      label: "New post",
      description: "Share an update, demo, or question",
      href: "/posts?compose=1",
      icon: MessageCircle,
    },
    {
      label: "Post a job",
      description: "Cast a role or workshop spot",
      href: "/jobs/new",
      icon: Briefcase,
    },
    {
      label: "Upload a demo",
      description: "Add to your profile portfolio",
      href: profileEditHref,
      icon: Mic,
    },
    {
      label: "Create a group",
      description: "Start a community or guild",
      href: "/groups/create",
      icon: Users,
    },
  ];

  const handleAction = (href: string) => {
    onClose();
    router.push(href);
  };

  return (
    <div
      className="md:hidden fixed inset-0 z-[60] flex items-end justify-center bg-surface-0/80 backdrop-blur-sm"
      onClick={onClose}
      aria-hidden={false}
    >
      <div
        ref={sheetRef}
        role="dialog"
        aria-label="Quick actions"
        aria-modal="true"
        className="w-full bg-surface-2 border-t border-border-subtle rounded-t-md p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] mb-[64px] animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-border-subtle" />
        <div className="flex flex-col">
          {actions.map((action, idx) => {
            const Icon = action.icon;
            return (
              <button
                key={action.href}
                ref={idx === 0 ? firstActionRef : undefined}
                type="button"
                onClick={() => handleAction(action.href)}
                aria-label={action.label}
                className="flex items-center gap-3 h-14 px-3 rounded-sm text-left active:bg-surface-3 hover:bg-surface-3 transition-colors"
              >
                <span className="w-10 h-10 rounded-sm bg-surface-3 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-text-primary" strokeWidth={1.75} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-text-primary text-sm font-medium truncate">
                    {action.label}
                  </span>
                  <span className="block text-text-secondary text-xs truncate">
                    {action.description}
                  </span>
                </span>
                <ChevronRight
                  className="w-4 h-4 text-text-tertiary flex-shrink-0"
                  strokeWidth={1.75}
                />
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-2 w-full h-12 rounded-sm bg-surface-3 text-text-primary text-sm font-medium hover:bg-surface-2 border border-border-subtle transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
